import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FaUsers, FaSearch, FaFilter, FaEye, FaTimes,
  FaCalendarAlt, FaChevronDown,
} from 'react-icons/fa';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  disabled: boolean;
  isDeleted: boolean;
  organization: { name: string; code: string };
  createdAt: string;
}

/* ─── helpers ─────────────────────────────────────────────────────────── */

const getStoredFilters = () => {
  try {
    const saved = sessionStorage.getItem('usersListFilters');
    if (saved) return JSON.parse(saved);
  } catch {}
  return {};
};

/**
 * "Joined" column shows user.createdAt as a short date.
 * Date-range filter passes two query params to the backend:
 *   joinedFrom  – ISO string for start of selected "from" day (00:00:00 UTC)
 *   joinedTo    – ISO string for end   of selected "to"   day (23:59:59 UTC)
 *
 * Backend should filter:  createdAt >= joinedFrom  AND  createdAt <= joinedTo
 */
const toISOStart = (dateStr: string) => {
  if (!dateStr) return '';
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString();
};
const toISOEnd = (dateStr: string) => {
  if (!dateStr) return '';
  return new Date(`${dateStr}T23:59:59.999Z`).toISOString();
};

const formatDisplayDate = (dateStr: string) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

/* ─── DateRangePicker ─────────────────────────────────────────────────── */

interface DateRangePickerProps {
  fromDate: string;
  toDate: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onClear: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  fromDate, toDate, onFromChange, onToChange, onClear,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasValue = !!(fromDate || toDate);

  const label = hasValue
    ? fromDate && toDate
      ? `${formatDisplayDate(fromDate)} → ${formatDisplayDate(toDate)}`
      : fromDate
      ? `From ${formatDisplayDate(fromDate)}`
      : `Until ${formatDisplayDate(toDate)}`
    : 'Joined date range';

  return (
    <div className="relative" ref={ref}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all ${
          hasValue
            ? 'border-primary bg-primary bg-opacity-5 text-primary'
            : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
        }`}
      >
        <FaCalendarAlt className={`w-3.5 h-3.5 ${hasValue ? 'text-primary' : 'text-gray-400'}`} />
        <span className="max-w-[200px] truncate">{label}</span>
        {hasValue ? (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onClear(); setOpen(false); }}
            className="ml-1 text-primary hover:text-red-500 transition-colors"
          >
            <FaTimes className="w-3 h-3" />
          </span>
        ) : (
          <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-xl border border-gray-100 p-5 w-80"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-primary bg-opacity-10 flex items-center justify-center">
                <FaCalendarAlt className="w-3.5 h-3.5 text-primary" />
              </div>
              <p className="text-sm font-semibold text-gray-800">Filter by Join Date</p>
            </div>

            <div className="space-y-3">
              {/* From */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  From
                </label>
                <input
                  type="date"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(e) => onFromChange(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50"
                />
              </div>

              {/* To */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  To
                </label>
                <input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(e) => onToChange(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50"
                />
              </div>
            </div>

            {/* Quick presets */}
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Quick Select</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: 'Today', getDates: () => {
                    const d = new Date().toISOString().slice(0, 10);
                    return { from: d, to: d };
                  }},
                  { label: 'Last 7 days', getDates: () => {
                    const to = new Date();
                    const from = new Date(); from.setDate(from.getDate() - 6);
                    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
                  }},
                  { label: 'Last 30 days', getDates: () => {
                    const to = new Date();
                    const from = new Date(); from.setDate(from.getDate() - 29);
                    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
                  }},
                  { label: 'This month', getDates: () => {
                    const now = new Date();
                    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
                    const to = now.toISOString().slice(0, 10);
                    return { from, to };
                  }},
                  { label: 'Last 3 months', getDates: () => {
                    const to = new Date();
                    const from = new Date(); from.setMonth(from.getMonth() - 3);
                    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
                  }},
                ].map(({ label, getDates }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => { const { from, to } = getDates(); onFromChange(from); onToChange(to); }}
                    className="px-2.5 py-1 bg-gray-100 hover:bg-primary hover:text-white text-gray-600 rounded-lg text-xs font-medium transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── UsersList ───────────────────────────────────────────────────────── */

const UsersList: React.FC = () => {
  const navigate = useNavigate();
  const storedFilters = useRef(getStoredFilters());

  const [searchInput, setSearchInput] = useState(storedFilters.current.searchTerm || '');
  const [searchTerm, setSearchTerm] = useState(storedFilters.current.searchTerm || '');
  const [roleFilter, setRoleFilter] = useState(storedFilters.current.roleFilter || '');
  const [accountStatusFilter, setAccountStatusFilter] = useState(storedFilters.current.accountStatusFilter || '');
  const [sortBy, setSortBy] = useState(storedFilters.current.sortBy || 'createdAt');
  const [sortOrder, setSortOrder] = useState(storedFilters.current.sortOrder || 'desc');
  const [currentPage, setCurrentPage] = useState(storedFilters.current.currentPage || 1);
  const [perPage, setPerPage] = useState(storedFilters.current.perPage || 10);
  // Date range for "Joined" column (stored as YYYY-MM-DD)
  const [joinedFrom, setJoinedFrom] = useState(storedFilters.current.joinedFrom || '');
  const [joinedTo, setJoinedTo] = useState(storedFilters.current.joinedTo || '');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const queryParams = {
    currentPage, perPage, searchTerm, roleFilter, accountStatusFilter,
    sortBy, sortOrder, joinedFrom, joinedTo,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['users', queryParams],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: perPage.toString(),
        sortBy,
        sortOrder,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(accountStatusFilter === 'active' && { disabled: 'false' }),
        ...(accountStatusFilter === 'disabled' && { disabled: 'true' }),
        ...(accountStatusFilter === 'deleted' && { isDeleted: 'true' }),
        // Date range — passed as ISO strings so backend can do a direct Date comparison
        ...(joinedFrom && { joinedFrom: toISOStart(joinedFrom) }),
        ...(joinedTo   && { joinedTo:   toISOEnd(joinedTo)     }),
      });
      const response = await api.get(`/admin/users?${params}`);
      const d = response.data.data || response.data;
      return {
        users: (d.users || []) as User[],
        totalPages: (d.pagination?.totalPages || 1) as number,
        totalUsers: (d.pagination?.totalUsers || 0) as number,
      };
    },
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });

  const users = data?.users || [];
  const totalPages = data?.totalPages || 1;
  const totalUsers = data?.totalUsers || 0;

  // Persist filters across navigation
  useEffect(() => {
    sessionStorage.setItem('usersListFilters', JSON.stringify({
      searchTerm, roleFilter, accountStatusFilter, sortBy, sortOrder,
      currentPage, perPage, joinedFrom, joinedTo,
    }));
  }, [searchTerm, roleFilter, accountStatusFilter, sortBy, sortOrder, currentPage, perPage, joinedFrom, joinedTo]);

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearchTerm(value); setCurrentPage(1); }, 500);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
    setCurrentPage(1);
  };

  const handlePerPageChange = (newPerPage: number) => { setPerPage(newPerPage); setCurrentPage(1); };

  const hasActiveFilters = !!(searchInput || roleFilter || accountStatusFilter || joinedFrom || joinedTo);

  const clearFilters = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput(''); setSearchTerm('');
    setRoleFilter(''); setAccountStatusFilter('');
    setSortBy('createdAt'); setSortOrder('desc');
    setJoinedFrom(''); setJoinedTo('');
    setCurrentPage(1); setPerPage(10);
    sessionStorage.removeItem('usersListFilters');
  };

  /* ─── display helpers ──── */
  const getStatusBadge = (user: User) => {
    if (user.isDeleted) return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Deleted</span>;
    if (user.disabled)  return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Disabled</span>;
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>;
  };

  const getRoleBadge = (role: string) => {
    const map: Record<string, { color: string; label: string }> = {
      admin:         { color: 'bg-red-100 text-red-800',    label: 'Admin' },
      agency_admin:  { color: 'bg-blue-100 text-blue-800',  label: 'Agency Admin' },
      agency_member: { color: 'bg-green-100 text-green-800', label: 'Agency Member' },
      member:        { color: 'bg-gray-100 text-gray-800',  label: 'Member' },
    };
    const cfg = map[role] || map.member;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
  };

  /**
   * "Joined" column — shows user.createdAt
   * Formatted as "May 29, 2026" (short month, numeric day + year, no time)
   */
  const formatJoinedDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <span className="text-gray-400 text-xs ml-1">⇅</span>;
    return sortOrder === 'asc'
      ? <span className="text-primary text-xs ml-1">▲</span>
      : <span className="text-primary text-xs ml-1">▼</span>;
  };

  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">{children}{getSortIcon(field)}</div>
    </th>
  );

  if (isLoading && !data) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header + Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg p-6"
      >
        {/* Title row */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
            <p className="text-gray-600 mt-1">Manage all users in your platform</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">{totalUsers}</p>
              <p className="text-sm text-gray-500">Total Users</p>
            </div>
            <div className="p-3 bg-primary bg-opacity-10 rounded-full">
              <FaUsers className="h-8 w-8 text-primary" />
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="flex flex-col lg:flex-row gap-3 mb-4">
          <form onSubmit={handleSearchSubmit} className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                placeholder="Search users by name, email, or organization..."
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </div>
          </form>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <FaTimes className="h-3.5 w-3.5" /> Clear Filters
            </button>
          )}
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <FaFilter className="h-3.5 w-3.5 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="agency_admin">Agency Admin</option>
              <option value="agency_member">Agency Member</option>
              <option value="member">Member</option>
            </select>
          </div>

          <select
            value={accountStatusFilter}
            onChange={(e) => { setAccountStatusFilter(e.target.value); setCurrentPage(1); }}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Account Status</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="deleted">Deleted</option>
          </select>

          {/* Date range picker */}
          <DateRangePicker
            fromDate={joinedFrom}
            toDate={joinedTo}
            onFromChange={(v) => { setJoinedFrom(v); setCurrentPage(1); }}
            onToChange={(v) => { setJoinedTo(v); setCurrentPage(1); }}
            onClear={() => { setJoinedFrom(''); setJoinedTo(''); setCurrentPage(1); }}
          />
        </div>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100 text-xs">
            <span className="text-gray-400 font-medium">Active filters:</span>
            {searchInput && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                Search: "{searchInput}"
                <FaTimes className="w-2.5 h-2.5 cursor-pointer hover:text-blue-900" onClick={() => { setSearchInput(''); setSearchTerm(''); }} />
              </span>
            )}
            {roleFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                Role: {roleFilter}
                <FaTimes className="w-2.5 h-2.5 cursor-pointer hover:text-emerald-900" onClick={() => setRoleFilter('')} />
              </span>
            )}
            {accountStatusFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                Status: {accountStatusFilter}
                <FaTimes className="w-2.5 h-2.5 cursor-pointer hover:text-orange-900" onClick={() => setAccountStatusFilter('')} />
              </span>
            )}
            {(joinedFrom || joinedTo) && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                <FaCalendarAlt className="w-2.5 h-2.5" />
                Joined: {joinedFrom ? formatDisplayDate(joinedFrom) : '…'} → {joinedTo ? formatDisplayDate(joinedTo) : '…'}
                <FaTimes className="w-2.5 h-2.5 cursor-pointer hover:text-purple-900" onClick={() => { setJoinedFrom(''); setJoinedTo(''); }} />
              </span>
            )}
          </div>
        )}
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg overflow-hidden"
      >
        <div className="overflow-x-auto relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-10">
              <LoadingSpinner size="md" />
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader field="name">User</SortableHeader>
                <SortableHeader field="role">Role</SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                {/* Joined = user.createdAt */}
                <SortableHeader field="createdAt">
                  <span className="flex items-center gap-1">
                    <FaCalendarAlt className="w-3 h-3 text-gray-400" /> Joined
                  </span>
                </SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <FaUsers className="w-10 h-10 text-gray-300" />
                      <p className="text-gray-500 font-medium">No users found</p>
                      <p className="text-gray-400 text-sm">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary to-primaryDark rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                          {getInitials(user.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate max-w-[180px]" title={user.name}>{user.name}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[180px]" title={user.email}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getRoleBadge(user.role)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.organization?.name || 'N/A'}</div>
                      <div className="text-xs text-gray-400">{user.organization?.code || ''}</div>
                    </td>
                    {/* Shows user.createdAt — the date the account was created */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatJoinedDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(user)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/users/${user._id}`)}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                        title="View Details"
                      >
                        <FaEye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-600">
                Showing <span className="font-semibold">{(currentPage - 1) * perPage + 1}</span>–
                <span className="font-semibold">{Math.min(currentPage * perPage, totalUsers)}</span> of{' '}
                <span className="font-semibold">{totalUsers}</span>
              </p>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">Show</label>
                <select
                  value={perPage}
                  onChange={(e) => handlePerPageChange(Number(e.target.value))}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-sm text-gray-500">per page</span>
              </div>
            </div>

            <nav className="inline-flex rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border-r border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {(() => {
                const pages: React.ReactNode[] = [];
                const max = 5;
                let start = Math.max(1, currentPage - Math.floor(max / 2));
                let end = Math.min(totalPages, start + max - 1);
                if (end - start < max - 1) start = Math.max(1, end - max + 1);

                if (start > 1) {
                  pages.push(<button key={1} onClick={() => setCurrentPage(1)} className="px-3 py-2 text-sm text-gray-500 bg-white border-r border-gray-200 hover:bg-gray-50">1</button>);
                  if (start > 2) pages.push(<span key="e1" className="px-3 py-2 text-sm text-gray-400 bg-white border-r border-gray-200">…</span>);
                }
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <button key={i} onClick={() => setCurrentPage(i)}
                      className={`px-3 py-2 text-sm font-medium border-r border-gray-200 ${currentPage === i ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                      {i}
                    </button>
                  );
                }
                if (end < totalPages) {
                  if (end < totalPages - 1) pages.push(<span key="e2" className="px-3 py-2 text-sm text-gray-400 bg-white border-r border-gray-200">…</span>);
                  pages.push(<button key={totalPages} onClick={() => setCurrentPage(totalPages)} className="px-3 py-2 text-sm text-gray-500 bg-white border-r border-gray-200 hover:bg-gray-50">{totalPages}</button>);
                }
                return pages;
              })()}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default UsersList;
